import React, { useEffect, useState } from "react";
import { Form, Formik, Field } from "formik";
import { StyledTldrLabel } from "../../_components/styled/styles";
import AiTemplatesSelect from "../AiTemplatesSelect";
import { analyticsTrackEvent } from "../../_utils/common";
import TldrCollpasibleSectionSettings from "../../_components/settings/TldrCollpasibleSectionSettings";
import { AILanguages, AITones } from "../../_utils/constants";
import TldrSelect from "../../_components/common/TldrSelect";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Spacer,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { FaMagic } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { TldrBeatLoader } from "../../_components/common/statelessView";
import { primayColor } from "../../_components/styled/variable";

const serviceDescription =
  "We are design and collaboration platform for marketing teams";

const EditorWorkspaceForm = ({
  prompt,
  lastRun,
  onComplete,
  onTemplateChange,
  resultsView,
  props,
  handleCollapsibleSectionClick,
}) => {
  const { t } = useTranslation(["ai"]);
  const { document } = props;
  const { selectedText, data, isInputFormCollapsed, generatingResults } =
    document;
  const [titleText, setTitleText] = useState("");
  const [descriptionCounter, setDescriptionCounter] = useState(
    lastRun.description?.length ?? 0
  );
  const [descriptionText, setDescriptionText] = useState("");
  const [keywordsText, setKeywordsText] = useState("");

  useEffect(() => {
    if (
      !prompt?.content?.description &&
      selectedText.length > 0 &&
      selectedText.length < 120
    ) {
      setTitleText(selectedText.length > 0 ? selectedText : data.payload.title);
    } else if (titleText?.length === 0) {
      setTitleText(data?.payload?.title ? data.payload.title : lastRun.title);
    }
    setDescriptionText(
      selectedText.length > 0 ? selectedText : data.payload?.description ?? ""
    );

    setKeywordsText(data.payload?.keywords ?? "");
  }, [selectedText]);

  useEffect(() => {
    setTitleText(data?.payload?.title ?? "");
    setDescriptionText(data?.payload?.description ?? "");
    setKeywordsText(data?.payload?.keywords ?? "");
  }, [data]);

  function validateDescription(value) {
    let error;
    if (!value || value.length < 10) {
      error = "Need to know a little bit more.";
    } else if (value.length > 600) {
      error = "No more than 600 characters are allowed.";
    }
    return error;
  }

  function validateTitle(value) {
    let error;
    if (!value) {
      error = "Please enter valid input.";
    } else if (value.length > 120) {
      error = "No more than 120 characters are allowed.";
    }
    return error;
  }

  function validateKeywords(value) {
    let error;
    if (!value || value.length === 0) {
      error = "Need to know a little bit more.";
    } else if (value.length > 600) {
      error = "No more than 600 characters are allowed.";
    }
    return error;
  }

  return (
    <Formik
      initialValues={{
        title: titleText,
        description: descriptionText,
        keywords: keywordsText,
        output: lastRun.output,
        tone: lastRun.tone ?? "",
      }}
      onSubmit={(values, { setSubmitting, setErrors }) => {
        let keywords = values["keywords"];
        if (!Array.isArray(keywords)) {
          values["keywords"] = keywords.split(",");
        }
        if (!values["title"]) {
          values["title"] = "";
        }
        analyticsTrackEvent("aiGenerate", { language: values.output });
        onComplete(prompt.id, values);
      }}
    >
      {({
        resetForm,
        isSubmitting,
        handleChange,
        setFieldValue,
        isValid,
        handleBlur,
        values
      }) => (
        <>
          <Form>
            <Box
              background="brand.glass_dark_08"
              borderRadius="8px"
              pt={3}
              px={3}
              pb={2}
            >
              <TldrCollpasibleSectionSettings
                collapse={isInputFormCollapsed}
                title={prompt?.title}
                description={prompt?.content?.heroDescription}
                icon={prompt?.content?.icon}
                iconColor={prompt?.content?.color}
                onClick={() => {
                  handleCollapsibleSectionClick();
                }}
              >
                <VStack spacing={3} align="stretch">
                  <Box className="input-group" mt={1}>
                    <div className="tldr-flex-grow">
                      <Box
                        as={StyledTldrLabel}
                        className="tldr-flex-grow"
                        mb={1}
                      >
                        {t("select_template")}
                      </Box>
                      <AiTemplatesSelect
                        onSelect={(selectedValue) => {
                          resetForm();
                          onTemplateChange(selectedValue);
                        }}
                        resultsView={resultsView}
                      />
                    </div>
                  </Box>

                  <div className="input-group">
                    {prompt?.content.tone && (
                      <Box className="tldr-flex-grow" mr={1}>
                        <Box
                          as={StyledTldrLabel}
                          className="tldr-flex-grow"
                          mb={1}
                        >
                          {t("select_tone")}
                        </Box>
                        <TldrSelect
                          options={AITones}
                          defaultOption={lastRun.tone}
                          resultsView={resultsView}
                          onSelect={(output) => {
                            setFieldValue("tone", output.value);
                          }}
                          value={values.tone}
                        />
                      </Box>
                    )}

                    <div className="tldr-flex-grow">
                      <Box
                        as={StyledTldrLabel}
                        className="tldr-flex-grow"
                        mb={1}
                      >
                        {t("output_lang")}
                      </Box>
                      <TldrSelect
                        options={AILanguages}
                        defaultOption={lastRun.output}
                        onSelect={(output) => {
                          setFieldValue("output", output.value);
                        }}
                        value={values.output}
                        resultsView={resultsView}
                      />
                    </div>
                  </div>

                  {prompt?.content.title && (
                    <Field name="title" validate={validateTitle}>
                      {({ field, form, meta }) => {
                        return (
                          <>
                            <FormControl isInvalid={meta.touched && meta.error}>
                              <FormLabel htmlFor="title">
                                <HStack>
                                  <span>{prompt?.content.title}</span>
                                  <Spacer />
                                  <span>{field?.value?.length ?? 0}/120</span>
                                </HStack>
                              </FormLabel>
                              <Input
                                size="md"
                                type="text"
                                placeholder={
                                  lastRun.title
                                    ? lastRun.title
                                    : prompt?.content.title_placeholder
                                }
                                aria-describedby="basic-addon1"
                                name="title"
                                onChange={(e) => {
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                                {...field}
                              />
                              {meta.touched && (
                                <FormErrorMessage>
                                  {meta.error}
                                </FormErrorMessage>
                              )}
                            </FormControl>
                          </>
                        );
                      }}
                    </Field>
                  )}

                  {prompt?.content.description && (
                    <Field name="description" validate={validateDescription}>
                      {({ field, form, meta }) => {
                        return (
                          <>
                            <FormControl isInvalid={meta.touched && meta.error}>
                              <FormLabel htmlFor="description">
                                <HStack>
                                  <span>{prompt?.content.description}</span>
                                  <Spacer />
                                  <span>{descriptionCounter}/600</span>
                                </HStack>
                              </FormLabel>
                              <Textarea
                                {...field}
                                placeholder={
                                  prompt?.content.description_ph
                                    ? prompt?.content.description_ph
                                    : serviceDescription
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  setDescriptionCounter(e.target.value.length);
                                }}
                                onBlur={handleBlur}
                                rows={6}
                              />
                              {meta.touched && (
                                <FormErrorMessage>
                                  {meta.error}
                                </FormErrorMessage>
                              )}
                            </FormControl>
                          </>
                        );
                      }}
                    </Field>
                  )}

                  {prompt?.content.keywords && (
                    <Field name="keywords" validate={validateKeywords}>
                      {({ field, form, meta }) => {
                        return (
                          <>
                            <FormControl isInvalid={meta.touched && meta.error}>
                              <FormLabel htmlFor="keywords">
                                {prompt?.content.keywords}
                              </FormLabel>
                              <Textarea
                                {...field}
                                placeholder={
                                  lastRun?.keywords
                                    ? lastRun.keywords
                                    : prompt?.content.keyword_ph
                                    ? prompt?.content.keyword_ph
                                    : t("brand_marketing_placeholder")
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                }}
                                onBlur={handleBlur}
                                rows={6}
                              />
                              {meta.touched && (
                                <FormErrorMessage>
                                  {meta.error}
                                </FormErrorMessage>
                              )}
                            </FormControl>
                          </>
                        );
                      }}
                    </Field>
                  )}
                </VStack>
                <Button
                  rightIcon={<FaMagic color="gray.500" />}
                  isLoading={generatingResults}
                  type="submit"
                  variant="solid"
                  disabled={!isValid || generatingResults}
                  size="md"
                  isFullWidth
                  mt={3}
                  spinner={<TldrBeatLoader bgColor={primayColor} />}
                >
                  {t("generate_btn")}
                </Button>
              </TldrCollpasibleSectionSettings>
            </Box>
          </Form>
        </>
      )}
    </Formik>
  );
};

export default EditorWorkspaceForm;
